import React from "react";
import { FOOD_RELIEF_PHOTOS } from "../data/content";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { motion } from "framer-motion";

/**
 * Homepage preview of the "Carpark to 8,000+" food-relief case study.
 *
 * The full five-stage narrative lives on the dedicated case-study page
 * (/case-studies/carpark-to-8000) — it is not duplicated here. The standalone
 * "Most leaders measure themselves…" quote has been retired on the homepage
 * because the same philosophy is already carried by the hero's "People
 * enabler." emphasis and the My Approach / Built to run without me section.
 */
export default function FeaturedCaseStudy() {
  return (
    <section className="bg-[#0B0B0C] relative grain" data-testid="featured-case-study">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 lg:py-20 relative">
        {/* Header spread */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-10 lg:mb-12">
          <div className="lg:col-span-7">
            <p className="eyebrow">Selected work · Case study</p>
            <h2 className="mt-4 font-display text-4xl md:text-5xl lg:text-6xl leading-[1] tracking-tight text-[#FAF9F6]">
              From a carpark{" "}<br />
              <span className="text-[#D8A936]">to 8,000+.</span>
            </h2>
          </div>
          <div className="lg:col-span-5 lg:pt-10">
            <p className="text-[#A9A9A6] text-[15.5px] leading-relaxed max-w-md">
              A folding table. A spreadsheet. A carpark. Twelve months later, a documented and scalable food-relief operation supporting more than 8,000 students weekly.
            </p>
          </div>
        </div>

        {/* Three-photo triptych — unchanged assets, captions and alt text */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4" data-testid="food-relief-triptych">
          {FOOD_RELIEF_PHOTOS.map((p, i) => (
            <motion.figure
              key={p.id}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              className="relative overflow-hidden border border-[#343437] bg-[#151517] aspect-[4/3] group"
              data-testid={`food-relief-photo-${p.id}`}
            >
              <img
                src={p.src}
                alt={p.caption}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-105"
              />
              <div
                className="absolute inset-x-0 bottom-0 h-2/3 pointer-events-none"
                style={{ background: "linear-gradient(to top, rgba(11,11,12,0.9) 0%, rgba(11,11,12,0) 100%)" }}
              />
              <figcaption className="absolute inset-x-5 bottom-5">
                <p className="font-display text-base md:text-lg text-[#FAF9F6] tracking-tight">{p.caption}</p>
                <p className="mt-1 text-[10px] tracking-[0.22em] uppercase text-[#D8A936]">{p.meta}</p>
              </figcaption>
            </motion.figure>
          ))}
        </div>

        {/* Compact editorial footer — tags + deep link to the full story */}
        <div className="mt-10 pt-7 border-t border-[#343437] flex items-center justify-between gap-6 flex-wrap">
          <p className="font-display text-[11.5px] tracking-[0.22em] uppercase text-[#A9A9A6]">
            Operations · Systems Design · Community Impact
          </p>
          <Link
            to="/case-studies/carpark-to-8000"
            data-testid="case-study-full-link"
            className="inline-flex items-center gap-2 text-[#D8A936] font-display text-[11.5px] tracking-[0.18em] uppercase border-b border-[#D8A936]/40 pb-1 hover:border-[#D8A936] transition-colors"
          >
            Explore the full case study <ArrowUpRight size={13} />
          </Link>
        </div>
      </div>
    </section>
  );
}
